// src/App.js
// --- COORDINADOR PRINCIPAL DE LA APLICACIÓN ---
// App.js es ahora un archivo limpio que solo orquesta páginas y estado global.

import React, { useState, useCallback, useEffect } from 'react';
import './styles/App.css';

// Páginas
import LandingPage from './pages/LandingPage';
import UserDetailsForm from './pages/UserDetailsForm';
import SpreadSelection from './pages/SpreadSelection';
import DrawingPhase from './pages/DrawingPhase';
import ReadingPage from './pages/ReadingPage';
import GuidePage from './pages/GuidePage';
import ZodiacPage from './pages/ZodiacPage';
import JournalPage from './pages/JournalPage';
import AccountPage from './pages/AccountPage';
import LegalPage from './pages/LegalPage';
import CoursesPage from './courses/CoursesPage';
import CourseDetailPage from './courses/CourseDetailPage';

// Componentes
import Navbar from './components/ui/Navbar';
import PaymentModal from './components/PaymentModal';
import LoadingSpinner from './components/ui/LoadingSpinner';
import NewAccountCodeModal from './components/NewAccountCodeModal';

// Servicios
import { ensureAccountCode, linkCourseToAccount, syncReadingToAccount, deleteReadingFromAccount, getLocalAccountCode } from './utils/accountService';

// ─── ESTADO GLOBAL ─────────────────────────────────────────────────────────────
function App() {
  // Estado del usuario
  const [userDetails, setUserDetails] = useState(() => {
    try {
      const stored = localStorage.getItem('tarotUserDetails');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // Estado de navegación
  const [appState, setAppState] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('status') === 'approved' && localStorage.getItem('pendingItem')) {
      return 'validando_pago';
    }
    return localStorage.getItem('tarotUserDetails') ? 'landing' : 'landing';
  });

  // Estado de funcionalidades
  const [freePremiumUsed, setFreePremiumUsed] = useState(() =>
    localStorage.getItem('freePremiumReadingUsed') === 'true'
  );
  const [aiStatus, setAiStatus] = useState('checking');
  const [selectedSpread, setSelectedSpread] = useState(null);
  const [currentReading, setCurrentReading] = useState(null);
  const [savedReadings, setSavedReadings] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [itemToPurchase, setItemToPurchase] = useState(null);
  const [toast, setToast] = useState(null);
  const [newAccountCode, setNewAccountCode] = useState(null);
  const [legalInitialTab, setLegalInitialTab] = useState('terminos');

  // ─── EFECTOS DE INICIALIZACIÓN ──────────────────────────────────────────────
  useEffect(() => {
    // Verificar estado del Oráculo AI
    const checkAiConnection = async () => {
      try {
        const response = await fetch('/api/check-ai');
        if (response.ok) {
          const data = await response.json();
          setAiStatus(data.status);
        } else {
          setAiStatus('offline');
        }
      } catch {
        setAiStatus('offline');
      }
    };
    checkAiConnection();

    // Cargar lecturas guardadas
    try {
      const stored = JSON.parse(localStorage.getItem('tarotReadings')) || [];
      setSavedReadings(stored);
    } catch {
      setSavedReadings([]);
    }
  }, []);

  // ─── TOAST NOTIFICATIONS ────────────────────────────────────────────────────
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // ─── HANDLERS DE PAGO ───────────────────────────────────────────────────────
  const handlePaymentSuccessForReading = useCallback((spread) => {
    setSelectedSpread(spread);
    setItemToPurchase(null);
    setAppState('drawing');
  }, []);

  const handlePaymentSuccessForCourse = useCallback((course) => {
    try {
      const purchasedCourses = JSON.parse(localStorage.getItem('purchasedCourses')) || {};
      purchasedCourses[course.id] = true;
      localStorage.setItem('purchasedCourses', JSON.stringify(purchasedCourses));
      showToast('¡Acceso al curso activado! ✨');
      setSelectedCourseId(course.id);
      setAppState('courseDetail');
    } catch {
      showToast('Error al procesar el acceso. Escribinos a support@tarotmistico.lat', 'error');
      setAppState('courses');
    } finally {
      setItemToPurchase(null);
    }

    // Vincular el curso a un código de recuperación (best-effort, no bloquea la compra)
    (async () => {
      try {
        const { code, isNew } = await ensureAccountCode(userDetails);
        if (isNew) setNewAccountCode(code);
        await linkCourseToAccount(code, course.id);
      } catch (err) {
        console.error('Error al vincular el curso a tu cuenta de recuperación:', err);
      }
    })();
  }, [showToast, userDetails]);

  // Validar pago de Mercado Pago (retorno desde redirect)
  useEffect(() => {
    if (appState === 'validando_pago') {
      const pendingItemJSON = localStorage.getItem('pendingItem');
      if (!pendingItemJSON) { setAppState('landing'); return; }
      const pendingItem = JSON.parse(pendingItemJSON);
      localStorage.removeItem('pendingItem');
      window.history.replaceState({}, document.title, window.location.pathname);

      // Registrar comisión de referido si hay una pendiente
      const pendingRef = localStorage.getItem('pendingReferral');
      if (pendingRef) {
        try {
          const refData = JSON.parse(pendingRef);
          fetch('/api/referrals?action=commission', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(refData),
          }).catch((e) => console.error('Error registrando comisión:', e));
        } catch (e) {
          console.error('Error procesando referido:', e);
        }
        localStorage.removeItem('pendingReferral');
      }

      if (pendingItem.modules) {
        handlePaymentSuccessForCourse(pendingItem);
      } else {
        handlePaymentSuccessForReading(pendingItem);
      }
    }
  }, [appState, handlePaymentSuccessForCourse, handlePaymentSuccessForReading]);

  // ─── HANDLERS DE NAVEGACIÓN ─────────────────────────────────────────────────
  const handleStart = (details) => {
    setUserDetails({ ...details });
    try {
      localStorage.setItem('tarotUserDetails', JSON.stringify({ name: details.name, birthDate: details.birthDate }));
    } catch (e) {
      console.error('Error guardando datos:', e);
    }
    setAppState(selectedSpread ? 'drawing' : 'spreadSelection');
  };

  const handleNavigate = useCallback((destination) => {
    if (destination === 'startFreeReading') {
      // Tirada del día: no requiere login previo, ir a formulario con tirada preseleccionada
      setSelectedSpread({ name: 'Lectura del Día', cards: 3, positions: ['Pasado', 'Presente', 'Futuro'], price: 0 });
      setAppState(userDetails ? 'drawing' : 'userDetails');
    } else if (destination === 'spreadSelection') {
      setSelectedSpread(null);
      setAppState(userDetails ? 'spreadSelection' : 'userDetails');
    } else if (destination.startsWith('legal')) {
      // Soporta 'legal' (pestaña por defecto) o 'legal:privacidad' / 'legal:reembolsos' para abrir una pestaña puntual
      const [, tabId] = destination.split(':');
      setLegalInitialTab(tabId || 'terminos');
      setAppState('legal');
    } else {
      setAppState(destination);
    }
  }, [userDetails]);

  const handleReturnToLanding = useCallback(() => {
    setCurrentReading(null);
    setSelectedSpread(null);
    setAppState('landing');
  }, []);

  const handleSelectSpread = useCallback((spread) => {
    if (spread.price === 0) {
      setSelectedSpread(spread);
      setAppState(userDetails ? 'drawing' : 'userDetails');
      return;
    }
    if (!freePremiumUsed) {
      showToast(`🎁 Usando tu lectura gratuita para "${spread.name}"`);
      localStorage.setItem('freePremiumReadingUsed', 'true');
      setFreePremiumUsed(true);
      setSelectedSpread(spread);
      setAppState(userDetails ? 'drawing' : 'userDetails');
      return;
    }
    setItemToPurchase(spread);
    setAppState('payment');
  }, [freePremiumUsed, userDetails, showToast]);

  const handlePurchaseCourse = useCallback((course) => {
    setItemToPurchase(course);
    setAppState('payment');
  }, []);

  const handleSelectCourse = useCallback((courseId) => {
    setSelectedCourseId(courseId);
    setAppState('courseDetail');
  }, []);

  const handleDrawComplete = useCallback((drawnCards) => {
    if (!selectedSpread) return;
    const isAiPowered = selectedSpread.name !== 'Lectura del Día' && selectedSpread.price >= 0;
    setCurrentReading({
      id: `reading_${Date.now()}`,
      spreadName: selectedSpread.name,
      spreadPositions: selectedSpread.positions,
      cards: drawnCards,
      interpretation: '',
      date: new Date().toISOString(),
      isAiPowered,
    });
    setAppState('reading');
    setSelectedSpread(null);
  }, [selectedSpread]);

  const saveReading = useCallback((readingToSave) => {
    const exists = savedReadings.some((r) => r.id === readingToSave.id);
    const updated = exists
      ? savedReadings.map((r) => (r.id === readingToSave.id ? readingToSave : r))
      : [readingToSave, ...savedReadings];
    setSavedReadings(updated);
    try {
      localStorage.setItem('tarotReadings', JSON.stringify(updated));
      showToast(exists ? '✏️ Lectura actualizada en tu diario' : '✅ Lectura guardada en tu diario');
    } catch {
      showToast('❌ No se pudo guardar la lectura', 'error');
    }

    // Sincronizar con un código de recuperación (best-effort, no bloquea el guardado local)
    (async () => {
      try {
        const { code, isNew } = await ensureAccountCode(userDetails);
        if (isNew) setNewAccountCode(code);
        await syncReadingToAccount(code, readingToSave);
      } catch (err) {
        console.error('Error al sincronizar la lectura con tu cuenta de recuperación:', err);
      }
    })();
  }, [savedReadings, showToast, userDetails]);

  const deleteReading = useCallback((id) => {
    const updated = savedReadings.filter((r) => r.id !== id);
    setSavedReadings(updated);
    localStorage.setItem('tarotReadings', JSON.stringify(updated));
    showToast('🗑️ Lectura eliminada');
    const code = getLocalAccountCode();
    if (code) deleteReadingFromAccount(code, id);
  }, [savedReadings, showToast]);

  const viewJournalReading = useCallback((reading) => {
    setCurrentReading(reading);
    setAppState('reading');
  }, []);

  // ─── RENDERIZADO DE PÁGINAS ──────────────────────────────────────────────────
  const renderPage = () => {
    switch (appState) {
      case 'landing':
        return <LandingPage onNavigate={handleNavigate} aiStatus={aiStatus} userDetails={userDetails} />;

      case 'userDetails':
        return <UserDetailsForm onStart={handleStart} initialDetails={userDetails} onNavigate={handleNavigate} />;

      case 'spreadSelection':
        return (
          <SpreadSelection
            onSelect={handleSelectSpread}
            onBack={handleReturnToLanding}
            freePremiumUsed={freePremiumUsed}
            aiStatus={aiStatus}
          />
        );

      case 'guide':
        return <GuidePage onBack={handleReturnToLanding} />;

      case 'zodiac':
        return <ZodiacPage onBack={handleReturnToLanding} aiStatus={aiStatus} userDetails={userDetails} />;

      case 'journal':
        return (
          <JournalPage
            readings={savedReadings}
            onBack={handleReturnToLanding}
            onDelete={deleteReading}
            onView={viewJournalReading}
          />
        );

      case 'account':
        return <AccountPage onBack={handleReturnToLanding} />;

      case 'legal':
        return <LegalPage onBack={handleReturnToLanding} initialTab={legalInitialTab} />;

      case 'courses':
        return (
          <CoursesPage
            onSelectCourse={handleSelectCourse}
            onPurchase={handlePurchaseCourse}
            onBack={handleReturnToLanding}
          />
        );

      case 'courseDetail':
        return (
          <CourseDetailPage
            courseId={selectedCourseId}
            onBack={() => setAppState('courses')}
            onPurchase={handlePurchaseCourse}
          />
        );

      case 'drawing':
        if (!selectedSpread) { setAppState('landing'); return null; }
        return <DrawingPhase spread={selectedSpread} onDrawComplete={handleDrawComplete} />;

      case 'reading':
        if (!currentReading) { setAppState('landing'); return null; }
        return (
          <ReadingPage
            reading={currentReading}
            onSave={saveReading}
            onBack={handleReturnToLanding}
            userDetails={userDetails}
          />
        );

      case 'payment': {
        const isForCourse = !!itemToPurchase?.modules;
        const successCallback = isForCourse
          ? () => handlePaymentSuccessForCourse(itemToPurchase)
          : () => handlePaymentSuccessForReading(itemToPurchase);
        const cancelState = isForCourse
          ? (selectedCourseId ? 'courseDetail' : 'courses')
          : 'spreadSelection';

        return (
          <>
            {renderPageForState(cancelState)}
            <PaymentModal
              onPaymentSuccess={successCallback}
              onCancel={() => { setItemToPurchase(null); setAppState(cancelState); }}
              item={itemToPurchase}
              onNavigate={handleNavigate}
            />
          </>
        );
      }

      case 'validando_pago':
        return (
          <div className="page-container">
            <LoadingSpinner message="Validando tu pago, un momento..." />
          </div>
        );

      default:
        return <LandingPage onNavigate={handleNavigate} aiStatus={aiStatus} userDetails={userDetails} />;
    }
  };

  const renderPageForState = (state) => {
    switch (state) {
      case 'courseDetail':
        return <CourseDetailPage courseId={selectedCourseId} onBack={() => setAppState('courses')} onPurchase={handlePurchaseCourse} />;
      case 'courses':
        return <CoursesPage onSelectCourse={handleSelectCourse} onPurchase={handlePurchaseCourse} onBack={handleReturnToLanding} />;
      default:
        return <SpreadSelection onSelect={handleSelectSpread} onBack={handleReturnToLanding} freePremiumUsed={freePremiumUsed} aiStatus={aiStatus} />;
    }
  };

  // Páginas donde mostrar la navbar
  const showNavbar = ['landing', 'spreadSelection', 'guide', 'zodiac', 'journal', 'courses', 'account', 'legal'].includes(appState);

  return (
    <div className="app-root">
      {showNavbar && <Navbar onNavigate={handleNavigate} currentPage={appState} />}

      <main className="app-main">
        {renderPage()}
      </main>

      {/* Toast Notification */}
      {toast && (
        <div className={`toast toast--${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Código de recuperación recién creado */}
      {newAccountCode && (
        <NewAccountCodeModal code={newAccountCode} onClose={() => setNewAccountCode(null)} />
      )}
    </div>
  );
}

export default App;
